import { loadClerk } from './clerk.ts';

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

/**
 * Fetch helper that auto-attaches the Clerk session JWT. Worker
 * endpoints under requireAuth all expect this header.
 */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const clerk = await loadClerk();
  const token = clerk.session ? await clerk.session.getToken() : null;

  const headers = new Headers(init.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  return fetch(`${BASE}${path}`, { ...init, headers });
}

export type CapThreshold = 'normal' | 'warn_80' | 'at_100' | 'over_150';

export interface AiUsageMeter {
  used_cents: number;
  cap_cents: number;
  /** integer percent; can exceed 100. Server-computed (worker llm/cap.ts). */
  pct: number;
  /** §D.3 band — drives banner UX without the client re-deriving 80/100/150. */
  threshold: CapThreshold;
  byok_enabled?: boolean;
}

export interface MeResponse {
  user: {
    id: string;
    email: string;
    name: string | null;
    role: 'admin' | 'member' | 'viewer' | 'founder';
    timezone: string | null;
  };
  tenant: {
    id: string;
    name: string;
    plan_tier: string;
    subscription_status: string;
    timezone: string;
    industry_template: string | null;
    is_design_partner: boolean;
    byok_enabled: boolean;
  };
  is_provisioning: boolean;
  ai_usage: {
    user: AiUsageMeter;
    system: AiUsageMeter;
    year_month: string;
  };
}

export async function getMe(): Promise<MeResponse> {
  const res = await apiFetch('/api/me');
  if (!res.ok) {
    throw new Error(`/api/me ${res.status}: ${await res.text()}`);
  }
  return res.json() as Promise<MeResponse>;
}

export interface CheckoutSessionResponse {
  url: string;
  session_id: string;
}

export async function createCheckoutSession(
  options: { dp?: boolean; coupon?: string } = {},
): Promise<CheckoutSessionResponse> {
  const params = new URLSearchParams();
  if (options.dp) params.set('dp', '1');
  if (options.coupon) params.set('coupon', options.coupon);
  const query = params.toString();
  const path = `/api/billing/checkout-session${query ? `?${query}` : ''}`;

  const res = await apiFetch(path, { method: 'POST' });
  if (!res.ok) {
    throw new Error(`/api/billing/checkout-session ${res.status}: ${await res.text()}`);
  }
  return res.json() as Promise<CheckoutSessionResponse>;
}

/**
 * Create a Stripe Customer Portal session and return its redirect URL.
 * The portal is Stripe-hosted (update card / cancel / invoices). Per
 * api.md §14 + onboarding §3.7.
 */
export async function getBillingPortalLink(): Promise<{ url: string }> {
  const res = await apiFetch('/api/billing/portal-link', { method: 'POST' });
  if (!res.ok) {
    throw new Error(`/api/billing/portal-link ${res.status}: ${await res.text()}`);
  }
  return res.json() as Promise<{ url: string }>;
}

// ---------------------------------------------------------------------------
// Settings — BYOK provider keys (byok-architecture §E.2)
// ---------------------------------------------------------------------------

export type ByokProvider = 'anthropic' | 'openai';

export interface ByokProviderMeta {
  provider: ByokProvider;
  /** masked hint, e.g. "sk-ant-•••••4f2a" — never the full key */
  key_hint: string;
  added_at: string;
}

export interface ByokSettings {
  ok: true;
  byok_enabled: boolean;
  providers: ByokProviderMeta[];
}

export async function getByokSettings(): Promise<ByokSettings> {
  const res = await apiFetch('/api/settings/byok');
  if (!res.ok) {
    throw new Error(`/api/settings/byok ${res.status}: ${await res.text()}`);
  }
  return res.json() as Promise<ByokSettings>;
}

export interface ConnectByokResponse {
  ok: true;
  provider: ByokProvider;
  key_hint: string;
  added_at: string;
}

/**
 * Connect a provider key. The worker validates the key with a live
 * low-token test call before storing — a 400 with an `error_code` means
 * validation failed (bad key, rate-limited, unsupported provider).
 */
export async function connectByok(
  provider: ByokProvider,
  apiKey: string,
): Promise<ConnectByokResponse> {
  const res = await apiFetch('/api/settings/byok', {
    method: 'POST',
    body: JSON.stringify({ provider, api_key: apiKey }),
  });
  const json = (await res.json()) as
    | ConnectByokResponse
    | { ok: false; error_code: string; message: string };
  if (!res.ok || !json.ok) {
    const message = 'message' in json ? json.message : `/api/settings/byok ${res.status}`;
    throw new Error(message);
  }
  return json;
}

export async function disconnectByok(provider: ByokProvider): Promise<ByokSettings> {
  const res = await apiFetch(`/api/settings/byok/${provider}`, { method: 'DELETE' });
  if (!res.ok) {
    throw new Error(`/api/settings/byok/${provider} ${res.status}: ${await res.text()}`);
  }
  return res.json() as Promise<ByokSettings>;
}

export interface TestByokResponse {
  ok: true;
  valid: boolean;
  reason: string | null;
}

export async function testByok(provider: ByokProvider): Promise<TestByokResponse> {
  const res = await apiFetch(`/api/settings/byok/${provider}/test`, { method: 'POST' });
  if (!res.ok) {
    throw new Error(`/api/settings/byok/${provider}/test ${res.status}: ${await res.text()}`);
  }
  return res.json() as Promise<TestByokResponse>;
}

// ---------------------------------------------------------------------------
// Onboarding wizard (onboarding.md §4-§6)
// ---------------------------------------------------------------------------

export type IndustryTemplateId = 'it_solutions' | 'wealth_advisor' | 'custom';

export interface SetIndustryTemplateResponse {
  ok: true;
  template: string;
  already_set?: boolean;
}

/**
 * Lock the tenant's industry template (onboarding.md §4.3). Idempotent —
 * re-picking returns `already_set: true` and changes nothing server-side.
 */
export async function setIndustryTemplate(
  template: IndustryTemplateId,
): Promise<SetIndustryTemplateResponse> {
  const res = await apiFetch('/api/onboarding/industry-template', {
    method: 'POST',
    body: JSON.stringify({ template }),
  });
  const json = (await res.json()) as
    | SetIndustryTemplateResponse
    | { ok: false; error_code: string; message: string };
  if (!res.ok || !json.ok) {
    const message =
      'message' in json ? json.message : `/api/onboarding/industry-template ${res.status}`;
    throw new Error(message);
  }
  return json;
}

export interface ImportCsvResponse {
  ok: true;
  imported: number;
  skipped_duplicates: number;
}

/**
 * Import accounts from a CSV. The raw CSV text is sent as the request body
 * (no R2 staging at MVP — B.2 deviation #1). A 400 carries an `error_code`
 * (`no_name_column` / `too_many_rows` / `validation_failed`) plus a
 * user-facing `message`.
 */
export async function importCsv(csvText: string): Promise<ImportCsvResponse> {
  const res = await apiFetch('/api/onboarding/import-csv', {
    method: 'POST',
    headers: { 'Content-Type': 'text/csv' },
    body: csvText,
  });
  const json = (await res.json()) as
    | ImportCsvResponse
    | { ok: false; error_code: string; message: string };
  if (!res.ok || !json.ok) {
    const message = 'message' in json ? json.message : `/api/onboarding/import-csv ${res.status}`;
    throw new Error(message);
  }
  return json;
}

export interface OnboardingAccount {
  id: string;
  type: 'account';
  name: string;
  metadata: Record<string, unknown>;
  created_at: string;
}

/**
 * Add a single account during onboarding / from the /today empty-state
 * (onboarding.md §5.6 / §6.3). Domain is optional.
 */
export async function addAccountOnboarding(args: {
  name: string;
  domain?: string;
}): Promise<OnboardingAccount> {
  const res = await apiFetch('/api/onboarding/add-account', {
    method: 'POST',
    body: JSON.stringify(args),
  });
  const json = (await res.json()) as
    | { ok: true; entity: OnboardingAccount }
    | { ok: false; error_code: string; message: string };
  if (!res.ok || !json.ok) {
    const message = 'message' in json ? json.message : `/api/onboarding/add-account ${res.status}`;
    throw new Error(message);
  }
  return json.entity;
}

/** "I'll add accounts later" — server-side no-op (B.2 wrinkle: no metadata flag). */
export async function skipImport(): Promise<void> {
  const res = await apiFetch('/api/onboarding/skip-import', { method: 'POST' });
  if (!res.ok) {
    throw new Error(`/api/onboarding/skip-import ${res.status}: ${await res.text()}`);
  }
}

/** Returns the tenant's account-entity count — drives the /today empty-state. */
export async function getAccountCount(): Promise<number> {
  const res = await apiFetch('/api/onboarding/account-count');
  if (!res.ok) {
    throw new Error(`/api/onboarding/account-count ${res.status}: ${await res.text()}`);
  }
  const json = (await res.json()) as { ok: true; account_count: number };
  return json.account_count;
}

// ---------------------------------------------------------------------------
// Capture
// ---------------------------------------------------------------------------

export interface ExtractedChip {
  entity_id: string;
  confidence: number;
  /** What the user said in the transcript. */
  name_mentioned: string;
  /** Canonical entity name (e.g. "John Nickle" when user said "John"). */
  entity_name: string;
}

export interface UnmatchedMention {
  name: string;
  type: 'account' | 'contact';
  confidence: number;
  reasoning: string;
}

export interface ExtractedNextAction {
  text: string;
  due_date: string | null;
  confidence: number;
}

export interface Extraction {
  account: ExtractedChip | null;
  contacts: ExtractedChip[];
  relationships: Array<{
    type: 'reports_to' | 'parent_of';
    from_entity_id: string;
    to_entity_id: string;
    confidence: number;
  }>;
  unmatched_mentions: UnmatchedMention[];
  next_action: ExtractedNextAction | null;
  observation_text: string;
  meta: {
    candidate_count: number;
    input_tokens: number;
    output_tokens: number;
    latency_ms: number;
    model: string;
  };
}

export async function extractCapture(args: {
  transcript: string;
  scope?: { entity_id: string } | null;
}): Promise<Extraction> {
  const res = await apiFetch('/api/captures/extract', {
    method: 'POST',
    body: JSON.stringify({ transcript: args.transcript, scope: args.scope ?? null }),
  });
  if (!res.ok) {
    throw new Error(`/api/captures/extract ${res.status}: ${await res.text()}`);
  }
  const json = (await res.json()) as {
    ok: boolean;
    extraction: Extraction;
    error_code?: string;
    message?: string;
  };
  if (!json.ok) throw new Error(json.message ?? 'extract failed');
  return json.extraction;
}

export interface CaptureSaveRequest {
  client_req_id: string;
  capture_method: 'voice_browser' | 'typed';
  capture_started_at: string;
  capture_duration_ms?: number;
  scope?: { entity_id: string } | null;
  chips: {
    account_id: string | null;
    contact_ids: string[];
    creates: Array<{ tmp_id: string; name: string; type: string }>;
    relationships: Array<{ type: 'reports_to' | 'parent_of'; from: string; to: string }>;
    next_action: { text: string; due_date: string | null } | null;
  };
  observation_text: string;
  extraction_metadata?: Record<string, unknown>;
}

export interface CaptureSaveResponse {
  ok: true;
  client_req_id: string;
  capture_id: string;
  duplicate?: boolean;
  observations: Array<{ id: string; entity_id: string; is_primary: boolean }>;
  entities_created: Array<{ tmp_id: string; id: string; name: string; type: string }>;
  next_action_id: string | null;
  primary: {
    observation_id: string;
    entity_id: string;
    entity_name: string;
    entity_type: string;
  } | null;
}

export async function saveCapture(req: CaptureSaveRequest): Promise<CaptureSaveResponse> {
  const res = await apiFetch('/api/captures', {
    method: 'POST',
    body: JSON.stringify(req),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`/api/captures ${res.status}: ${text}`);
  }
  return res.json() as Promise<CaptureSaveResponse>;
}

export interface RecentObservation {
  id: string;
  entity_id: string;
  entity_name: string;
  entity_type: string;
  capture_id: string;
  content: string;
  capture_method: string;
  is_primary: boolean;
  observed_at: string;
  created_at: string;
}

export async function getRecentCaptures(): Promise<RecentObservation[]> {
  const res = await apiFetch('/api/captures/recent');
  if (!res.ok) {
    throw new Error(`/api/captures/recent ${res.status}: ${await res.text()}`);
  }
  const json = (await res.json()) as { observations: RecentObservation[] };
  return json.observations;
}

// ---------------------------------------------------------------------------
// Entities (browse / detail)
// ---------------------------------------------------------------------------

export interface EntityListItem {
  id: string;
  type: string;
  name: string;
  metadata: Record<string, unknown>;
  last_observation_at: string | null;
  observation_count: number;
  created_at: string;
}

export async function listEntities(
  args: { type?: 'account' | 'contact' | 'project' } = {},
): Promise<EntityListItem[]> {
  const params = new URLSearchParams();
  if (args.type) params.set('type', args.type);
  const query = params.toString();
  const res = await apiFetch(`/api/entities${query ? `?${query}` : ''}`);
  if (!res.ok) throw new Error(`/api/entities ${res.status}: ${await res.text()}`);
  const json = (await res.json()) as { entities: EntityListItem[] };
  return json.entities;
}

export interface EntityDetailResponse {
  entity: {
    id: string;
    type: string;
    namespace: string;
    name: string;
    content: string | null;
    metadata: Record<string, unknown>;
    created_at: string;
    updated_at: string;
  };
  observations: Array<{
    id: string;
    content: string;
    capture_method: string;
    observed_at: string;
    source: string;
    is_primary: boolean;
  }>;
  relationships: Array<{
    id: string;
    relation_type: string;
    direction: 'outgoing' | 'incoming';
    counterpart: { id: string; name: string; type: string };
    created_at: string;
  }>;
  next_actions: Array<{
    id: string;
    text: string;
    sub: string | null;
    color: 'green' | 'yellow' | 'red' | null;
    due_date: string | null;
    position: number;
    created_at: string;
  }>;
}

export async function getEntity(id: string): Promise<EntityDetailResponse> {
  const res = await apiFetch(`/api/entities/${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error(`/api/entities/${id} ${res.status}: ${await res.text()}`);
  return res.json() as Promise<EntityDetailResponse>;
}

export interface CreateEntityArgs {
  type: 'account' | 'contact' | 'project';
  name: string;
  metadata?: Record<string, unknown>;
}

export async function createEntity(args: CreateEntityArgs): Promise<EntityListItem> {
  const res = await apiFetch('/api/entities', {
    method: 'POST',
    body: JSON.stringify(args),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`POST /api/entities ${res.status}: ${text}`);
  }
  const json = (await res.json()) as { ok: boolean; entity: EntityListItem };
  return json.entity;
}

export interface PatchEntityArgs {
  name?: string;
  metadata?: Record<string, unknown>;
  content?: string | null;
}

export async function patchEntity(
  id: string,
  args: PatchEntityArgs,
): Promise<EntityDetailResponse['entity']> {
  const res = await apiFetch(`/api/entities/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(args),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`PATCH /api/entities/${id} ${res.status}: ${text}`);
  }
  const json = (await res.json()) as { ok: boolean; entity: EntityDetailResponse['entity'] };
  return json.entity;
}

export interface DeleteEntityResponse {
  ok: true;
  entity_id: string;
  deleted_at: string;
  vectors_removed: number;
}

export async function deleteEntity(id: string): Promise<DeleteEntityResponse> {
  const res = await apiFetch(`/api/entities/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`DELETE /api/entities/${id} ${res.status}: ${text}`);
  }
  return res.json() as Promise<DeleteEntityResponse>;
}

export interface RestoreEntityResponse {
  ok: true;
  entity_id: string;
  restored_at: string;
  counts: {
    observations: number;
    relationships: number;
    next_actions: number;
    triggers: number;
    captures: number;
  };
  vectors_enqueued: number;
}

export async function restoreEntity(id: string): Promise<RestoreEntityResponse> {
  const res = await apiFetch(`/api/entities/${encodeURIComponent(id)}/restore`, {
    method: 'POST',
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`POST /api/entities/${id}/restore ${res.status}: ${text}`);
  }
  return res.json() as Promise<RestoreEntityResponse>;
}

export interface ObservationDetail {
  id: string;
  entity_id: string;
  capture_id: string;
  content: string;
  source: string;
  capture_method: string;
  is_primary: boolean;
  observed_at: string;
  created_at: string;
}

export async function patchObservation(
  id: string,
  args: { content: string },
): Promise<ObservationDetail> {
  const res = await apiFetch(`/api/observations/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(args),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`PATCH /api/observations/${id} ${res.status}: ${text}`);
  }
  const json = (await res.json()) as { ok: boolean; observation: ObservationDetail };
  return json.observation;
}

export interface PreflightMatch {
  entity_id: string;
  name: string;
  type: string;
  lex_score: number;
}

export async function preflightEntity(
  name: string,
  type: 'account' | 'contact' | 'project',
): Promise<PreflightMatch[]> {
  const params = new URLSearchParams({ name, type });
  const res = await apiFetch(`/api/entities/preflight?${params.toString()}`);
  if (!res.ok) {
    // Preflight failure is non-fatal — the client falls through to the
    // normal Create flow without the soft warning. Return [] so the
    // caller can keep going.
    return [];
  }
  const json = (await res.json()) as { matches: PreflightMatch[] };
  return json.matches;
}

// ---------------------------------------------------------------------------
// Briefing
// ---------------------------------------------------------------------------

export interface BriefingArtifact {
  schema_version: 1;
  tenant_id: string;
  user_id: string;
  briefing_date: string;
  tier: 'active' | 'idle' | 'churned';
  tenant_timezone: string;
  weather_line: string;
  stats: {
    captures_yesterday: number;
    observations_yesterday: number;
    open_next_actions: number;
    active_accounts_count: number;
  };
  triggers: never[];
  going_cold: Array<{
    entity_id: string;
    name: string;
    type: string;
    tier: string | null;
    cadence_days: number;
    days_since_touch: number;
    freshness: 'fresh' | 'aging' | 'stale';
  }>;
  next_actions_due: Array<{
    id: string;
    text: string;
    sub: string | null;
    entity_id: string;
    entity_name: string;
    due_date: string;
    is_overdue: boolean;
  }>;
  what_changed: null;
  metadata: {
    model: string;
    model_routing: 'pooled' | 'byok';
    feature_class: 'system';
    generated_at: string;
    input_tokens: number;
    output_tokens: number;
    fallback: boolean;
    fallback_reason: string | null;
  };
}

export async function getBriefing(): Promise<{
  briefing: BriefingArtifact;
  cache: 'hit' | 'miss';
}> {
  const res = await apiFetch('/api/briefing/today');
  if (!res.ok) throw new Error(`/api/briefing/today ${res.status}: ${await res.text()}`);
  return res.json() as Promise<{ briefing: BriefingArtifact; cache: 'hit' | 'miss' }>;
}

// ---------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------

export interface SearchResult {
  observation_id: string;
  entity_id: string;
  entity_name: string;
  entity_type: string;
  content: string;
  capture_method: string;
  observed_at: string;
  score: number;
  /**
   * Which side of the hybrid search returned this row:
   *   - 'lexical' — literal token in content or entity name (score = 1.0)
   *   - 'semantic' — Vectorize cosine similarity above threshold
   * Lexical hits always sort first.
   */
  match: 'lexical' | 'semantic';
}

// ---------------------------------------------------------------------------
// Next actions (Board)
// ---------------------------------------------------------------------------

export interface NextAction {
  id: string;
  entity_id: string;
  text: string;
  sub: string | null;
  color: 'green' | 'yellow' | 'red' | null;
  due_date: string | null;
  status: 'pending' | 'done' | 'dismissed';
  position: number;
  created_at: string;
  updated_at: string;
}

export async function listNextActions(options: { entity_id?: string } = {}): Promise<NextAction[]> {
  const params = new URLSearchParams();
  if (options.entity_id) params.set('entity_id', options.entity_id);
  const q = params.toString();
  const res = await apiFetch(`/api/next-actions${q ? `?${q}` : ''}`);
  if (!res.ok) throw new Error(`/api/next-actions ${res.status}: ${await res.text()}`);
  const json = (await res.json()) as { next_actions: NextAction[] };
  return json.next_actions;
}

export async function createNextAction(args: {
  entity_id: string;
  text: string;
  sub?: string | null;
  color?: 'green' | 'yellow' | 'red' | null;
  due_date?: string | null;
  position?: number;
}): Promise<NextAction> {
  const res = await apiFetch('/api/next-actions', {
    method: 'POST',
    body: JSON.stringify(args),
  });
  if (!res.ok) throw new Error(`POST /api/next-actions ${res.status}: ${await res.text()}`);
  const json = (await res.json()) as { next_action: NextAction };
  return json.next_action;
}

export async function patchNextAction(
  id: string,
  args: {
    text?: string;
    sub?: string | null;
    color?: 'green' | 'yellow' | 'red' | null;
    due_date?: string | null;
    position?: number;
  },
): Promise<NextAction> {
  const res = await apiFetch(`/api/next-actions/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(args),
  });
  if (!res.ok) {
    throw new Error(`PATCH /api/next-actions/${id} ${res.status}: ${await res.text()}`);
  }
  const json = (await res.json()) as { next_action: NextAction };
  return json.next_action;
}

export async function completeNextAction(id: string): Promise<NextAction> {
  const res = await apiFetch(`/api/next-actions/${encodeURIComponent(id)}/complete`, {
    method: 'POST',
  });
  if (!res.ok) {
    throw new Error(`complete ${res.status}: ${await res.text()}`);
  }
  const json = (await res.json()) as { next_action: NextAction };
  return json.next_action;
}

export async function dismissNextAction(id: string): Promise<NextAction> {
  const res = await apiFetch(`/api/next-actions/${encodeURIComponent(id)}/dismiss`, {
    method: 'POST',
  });
  if (!res.ok) {
    throw new Error(`dismiss ${res.status}: ${await res.text()}`);
  }
  const json = (await res.json()) as { next_action: NextAction };
  return json.next_action;
}

export async function reorderNextActions(
  items: Array<{ id: string; position: number }>,
): Promise<void> {
  const res = await apiFetch('/api/next-actions/reorder', {
    method: 'POST',
    body: JSON.stringify({ items }),
  });
  if (!res.ok) {
    throw new Error(`reorder ${res.status}: ${await res.text()}`);
  }
}

export async function searchObservations(
  query: string,
  options: { topK?: number; entity_id_filter?: string } = {},
): Promise<SearchResult[]> {
  const res = await apiFetch('/api/observations/search', {
    method: 'POST',
    body: JSON.stringify({
      query,
      topK: options.topK,
      entity_id_filter: options.entity_id_filter,
    }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`/api/observations/search ${res.status}: ${text}`);
  }
  const json = (await res.json()) as { results: SearchResult[] };
  return json.results;
}
